import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { MENU_SAVORY, MENU_FRESH, IMAGES } from "@/data/site";

const Section = ({ data, image, imageRight }) => (
  <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
      <div className={`lg:col-span-4 lg:sticky lg:top-28 ${imageRight ? "lg:order-2" : ""}`}>
        <Reveal>
          <div className="relative aspect-[4/5] overflow-hidden border border-line">
            <img src={image} alt={data.label} className="w-full h-full object-cover contrast-125 grayscale-[10%]" />
            <span className="absolute left-0 bottom-0 bg-harissa text-base font-mono text-xs uppercase tracking-widest px-4 py-2">{data.label}</span>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-8">
        <Reveal>
          <h2 className="font-head font-[900] text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">{data.label}.</h2>
          <p className="font-sans text-muted mt-4 max-w-xl">{data.sub}</p>
        </Reveal>

        <div className="mt-10 border-t border-line">
          {data.items.map((item, i) => (
            <Reveal key={item.n} delay={i * 0.05}>
              <div className="group border-b border-line py-7 px-1 sm:px-3 hover:bg-surface transition-colors duration-300" data-testid={`menu-item-${item.n}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-2xl text-muted group-hover:text-harissa transition-colors">{item.n}</span>
                    <div>
                      <h3 className="font-head font-[800] text-2xl sm:text-3xl uppercase leading-none">{item.name}</h3>
                      <p className="font-mono text-xs uppercase tracking-widest text-muted mt-2">{item.origin}</p>
                    </div>
                  </div>
                  <span className="font-mono text-lg sm:text-xl text-mango whitespace-nowrap">CHF {item.price}</span>
                </div>
                <p className="font-sans text-muted mt-4 max-w-2xl sm:pl-10">{item.desc}</p>
                <div className="flex gap-2 mt-4 sm:pl-10">
                  {item.tags.map((t) => (
                    <span key={t} className="font-mono text-[10px] uppercase tracking-widest border border-line px-2 py-1 text-muted inline-flex items-center gap-1">
                      {t.includes("scharf") && <Flame size={11} className="text-harissa" />}{t}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default function MenuPage() {
  return (
    <div data-testid="menu-page" className="pt-28 sm:pt-36">
      <header className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-harissa mb-5">Die Karte</p>
        <h1 className="font-display uppercase text-[16vw] sm:text-[13vw] leading-[0.82] tracking-tight">
          <MaskLines lines={["Herzhaft.", <span key="s" className="text-stroke">Frisch.</span>]} />
        </h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="font-sans text-lg text-muted mt-6 max-w-xl">
          Sechs Gerichte, kein Füllmaterial. Jedes für sich ein Grund herzukommen.
        </motion.p>
      </header>

      <div className="mt-10"><Marquee /></div>

      <Section data={MENU_SAVORY} image={IMAGES.darkDish} />
      <div className="bg-surface border-y border-line">
        <Section data={MENU_FRESH} image={IMAGES.fruit} imageRight />
      </div>

      {/* Drinks */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center border border-line p-8 sm:p-14">
          <div className="md:col-span-8">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-mango mb-4">Getränke</p>
            <h2 className="font-head font-[900] text-3xl sm:text-5xl uppercase leading-[0.95]">Von der Bar.</h2>
            <p className="font-sans text-muted mt-5 max-w-lg">Cocktails, Wein, Bier &amp; Alkoholfreies. Das Essen passt dazu, nicht umgekehrt.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/reservierung" className="inline-flex items-center gap-2 bg-harissa text-base font-mono text-sm uppercase tracking-widest px-7 py-4 hover:bg-ink transition-colors" data-testid="menu-reserve-btn">
              Platz sichern <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
